<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else class="affordability-view-container">
    <MainHeader :title="'Can I Afford This?'" :showBack="true">
      <template #left>
        <font-awesome-icon
          @click="router.push({ name: 'Calculators' })"
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
        />
      </template>
    </MainHeader>

    <div class="view content content-padding">
      <!-- Intro -->
      <div class="intro-card">
        <div class="intro-icon">
          <font-awesome-icon :icon="['fas', 'circle-question']" />
        </div>
        <p class="intro-text">
          Test a purchase against your budget, goals, and safety net before you
          buy.
        </p>
      </div>

      <!-- Mode toggle -->
      <div class="mode-toggle">
        <button
          type="button"
          class="mode-btn"
          :class="{ active: mode === 'oneTime' }"
          @click="mode = 'oneTime'"
        >
          <font-awesome-icon :icon="['fas', 'tag']" class="mode-icon" />
          One-Time
        </button>
        <button
          type="button"
          class="mode-btn"
          :class="{ active: mode === 'recurring' }"
          @click="mode = 'recurring'"
        >
          <font-awesome-icon
            :icon="['fas', 'arrows-rotate']"
            class="mode-icon"
          />
          Recurring
        </button>
      </div>

      <!-- Input section -->
      <div class="ud-section">
        <span class="section-label">The Purchase</span>

        <div class="wrapper-card">
          <!-- What is it -->
          <div class="ud-field">
            <label class="ud-label">What is it?</label>
            <input
              v-model="purchaseName"
              type="text"
              class="input"
              :placeholder="
                mode === 'oneTime' ? 'New laptop, vacation…' : 'Streaming, gym…'
              "
            />
          </div>

          <!-- Amount -->
          <div class="ud-field mt">
            <label class="ud-label">
              {{ mode === 'oneTime' ? 'Cost' : 'Monthly cost' }}
            </label>
            <div class="currency-input">
              <span class="currency-symbol">$</span>
              <input
                v-model.number="amount"
                type="number"
                class="input currency-field"
                placeholder="0"
                inputmode="decimal"
                min="0"
              />
            </div>
          </div>

          <!-- Recurring frequency note -->
          <div v-if="mode === 'recurring' && amount > 0" class="recurring-note">
            <font-awesome-icon
              :icon="['fas', 'circle-info']"
              class="note-icon"
            />
            <span>
              Annual cost: <strong>${{ formatNumber(amount * 12) }}</strong>
            </span>
          </div>

          <!-- Budget category — accordion-style dropdown with subcategories -->
          <div class="ud-field mt" ref="dropdownRef">
            <label class="ud-label">Budget category (optional)</label>
            <button
              type="button"
              class="form-select grey-outline-form-select category-trigger"
              :class="{ open: dropdownOpen }"
              @click="dropdownOpen = !dropdownOpen"
              :disabled="loadingBudget"
            >
              <span
                class="category-trigger-value"
                :class="{ placeholder: !selectedNode }"
              >
                {{ triggerLabel }}
              </span>
              <font-awesome-icon
                :icon="['fas', 'chevron-down']"
                class="category-chevron"
                :class="{ open: dropdownOpen }"
              />
            </button>

            <transition name="sub-slide">
              <div v-if="dropdownOpen" class="category-menu">
                <!-- None option -->
                <button
                  type="button"
                  class="category-option"
                  :class="{ selected: !selectedNode }"
                  @click="selectNone"
                >
                  <span class="option-name">— None / not budgeted —</span>
                </button>

                <!-- Categories with subcategories nested -->
                <template
                  v-for="cat in spendingCategories"
                  :key="cat.categoryID"
                >
                  <button
                    type="button"
                    class="category-option category-parent"
                    :class="{
                      selected:
                        selectedNode &&
                        selectedNode.type === 'category' &&
                        selectedNode.id === cat.categoryID,
                    }"
                    @click="selectCategory(cat)"
                  >
                    <span
                      class="option-icon-wrap"
                      :style="iconStyle(cat.iconColor)"
                    >
                      <font-awesome-icon
                        :icon="parseIcon(cat.icon)"
                        class="option-icon"
                      />
                    </span>
                    <span class="option-name">{{ cat.name }}</span>
                    <span
                      v-if="parentDisplayAvailable(cat) !== null"
                      class="option-remaining"
                      :class="{ negative: parentDisplayAvailable(cat) < 0 }"
                    >
                      ${{ formatNumber(parentDisplayAvailable(cat)) }} left
                    </span>
                  </button>

                  <button
                    v-for="sub in cat.subcategories || []"
                    :key="sub.subcategoryID"
                    type="button"
                    class="category-option category-sub"
                    :class="{
                      selected:
                        selectedNode &&
                        selectedNode.type === 'subcategory' &&
                        selectedNode.id === sub.subcategoryID,
                    }"
                    @click="selectSubcategory(cat, sub)"
                  >
                    <span class="sub-indent">↳</span>
                    <span
                      class="option-icon-wrap"
                      :style="iconStyle(sub.iconColor)"
                    >
                      <font-awesome-icon
                        :icon="parseIcon(sub.icon)"
                        class="option-icon"
                      />
                    </span>
                    <span class="option-name">{{ sub.name }}</span>
                    <span
                      class="option-remaining"
                      :class="{ negative: sub.available < 0 }"
                    >
                      ${{ formatNumber(sub.available) }} left
                    </span>
                  </button>
                </template>

                <!-- Loading state -->
                <div v-if="loadingBudget" class="category-empty">
                  Loading your budget…
                </div>

                <!-- Empty state -->
                <div
                  v-if="!loadingBudget && spendingCategories.length === 0"
                  class="category-empty"
                >
                  <span v-if="loadError">{{ loadError }}</span>
                  <span v-else>No budget categories yet for this month.</span>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Verdict -->
      <div v-if="amount > 0 && verdict" class="ud-section">
        <div class="verdict-card" :class="`verdict-${verdict.tier}`">
          <div class="verdict-header">
            <div class="verdict-badge" :class="`verdict-${verdict.tier}`">
              <font-awesome-icon :icon="['fas', verdict.icon]" />
            </div>
            <div class="verdict-text">
              <p class="verdict-title">{{ verdict.title }}</p>
              <p class="verdict-summary">{{ verdict.summary }}</p>
            </div>
          </div>

          <!-- Suggested switch action -->
          <div v-if="verdict.suggestion" class="verdict-suggestion">
            <font-awesome-icon
              :icon="['fas', 'lightbulb']"
              class="suggestion-icon"
            />
            <div class="suggestion-text">
              <p class="suggestion-message">{{ verdict.suggestion.message }}</p>
              <button
                type="button"
                class="suggestion-action"
                @click="applySuggestion(verdict.suggestion)"
              >
                Switch to {{ verdict.suggestion.targetName }} →
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Signal breakdown -->
      <div v-if="amount > 0" class="ud-section">
        <span class="section-label">The Breakdown</span>

        <div class="wrapper-card signals-card">
          <div v-if="selectedNode" class="signal-row">
            <div class="signal-icon-wrap" :class="signalTone('budget')">
              <font-awesome-icon
                :icon="['fas', signalIcon('budget')]"
                class="signal-icon"
              />
            </div>
            <div class="signal-text">
              <p class="signal-title">{{ signals.budget.title }}</p>
              <p class="signal-detail">{{ signals.budget.message }}</p>
            </div>
          </div>

          <div v-if="emergencyFundEnabled" class="signal-row">
            <div class="signal-icon-wrap" :class="signalTone('emergency')">
              <font-awesome-icon
                :icon="['fas', signalIcon('emergency')]"
                class="signal-icon"
              />
            </div>
            <div class="signal-text">
              <p class="signal-title">Emergency fund</p>
              <p class="signal-detail">{{ signals.emergency.message }}</p>
            </div>
          </div>

          <div v-if="hasGoals" class="signal-row">
            <div class="signal-icon-wrap" :class="signalTone('goals')">
              <font-awesome-icon
                :icon="['fas', signalIcon('goals')]"
                class="signal-icon"
              />
            </div>
            <div class="signal-text">
              <p class="signal-title">Savings goals</p>
              <p class="signal-detail">{{ signals.goals.message }}</p>
            </div>
          </div>

          <div v-if="hasDebt" class="signal-row">
            <div class="signal-icon-wrap" :class="signalTone('debt')">
              <font-awesome-icon
                :icon="['fas', signalIcon('debt')]"
                class="signal-icon"
              />
            </div>
            <div class="signal-text">
              <p class="signal-title">Debt payoff</p>
              <p class="signal-detail">{{ signals.debt.message }}</p>
            </div>
          </div>

          <div
            v-if="
              !selectedNode && !emergencyFundEnabled && !hasGoals && !hasDebt
            "
            class="signal-empty"
          >
            <font-awesome-icon :icon="['fas', 'lightbulb']" />
            <p>
              Connect a budget category, set up an emergency fund, or add a
              savings goal to see deeper insights.
            </p>
          </div>
        </div>
      </div>

      <!-- Reset -->
      <button
        v-if="amount > 0"
        class="teal-outline-button w-100 pill-button"
        @click="resetCalculator"
      >
        Start Over
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useMyStore } from '../../store';
import MainHeader from '../../components/View-Header.vue';
import { useRouter } from 'vue-router';

export default {
  components: { MainHeader },
  setup() {
    const store = useMyStore();

    // ============================================================
    // BUDGET DATA — fetched via store action
    // ============================================================
    const categoryBudgets = ref([]);
    const monthlyBudgetMeta = ref(null); // plannedIncome, plannedExpenses, etc.
    const loadingBudget = ref(false);
    const loadError = ref('');
    const router = useRouter();

    const fetchMonthlyBudget = async () => {
      loadingBudget.value = true;
      loadError.value = '';
      try {
        const today = new Date();
        const month = today.getMonth() + 1; // JS months are 0-indexed; API expects 1-12
        const year = today.getFullYear();

        const response = await store.getMonthlyBudgetDataForAllCategories(
          month,
          year
        );
        const data = response?.data || {};
        categoryBudgets.value = data.categoryBudgets || [];
        monthlyBudgetMeta.value = {
          plannedIncome: data.plannedIncome || 0,
          actualIncome: data.actualIncome || 0,
          plannedExpenses: data.plannedExpenses || 0,
          actualExpenses: data.actualExpenses || 0,
          uncategorizedExpenses: data.uncategorizedExpenses || 0,
          uncategorizedTransactionCount:
            data.uncategorizedTransactionCount || 0,
        };
      } catch (err) {
        console.error('Failed to load monthly budget for affordability', err);
        loadError.value = "Couldn't load your budget. Pull down to retry.";
        categoryBudgets.value = [];
      } finally {
        loadingBudget.value = false;
      }
    };

    // 🔧 KELLY — these other data sources still need to be wired when those
    // features exist in the app. For now they default to empty.
    const emergencyFund = computed(() => store.emergencyFund || null);
    const savingsGoals = computed(() => store.savingsGoals || []);
    const debts = computed(() => store.debts || []);

    // ============================================================
    // STATE
    // ============================================================
    const mode = ref('oneTime');
    const purchaseName = ref('');
    const amount = ref(0);
    const selectedNode = ref(null);
    const dropdownOpen = ref(false);
    const dropdownRef = ref(null);

    // ============================================================
    // CATEGORIES — filter income, sort
    // ============================================================
    const spendingCategories = computed(() =>
      categoryBudgets.value
        .filter((c) => !c.income)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
    );

    const findCategoryById = (id) =>
      categoryBudgets.value.find((c) => c.categoryID === id);

    const findSubcategoryById = (subId) => {
      for (const cat of categoryBudgets.value) {
        const sub = (cat.subcategories || []).find(
          (s) => s.subcategoryID === subId
        );
        if (sub) return { sub, parent: cat };
      }
      return null;
    };

    const selectedCategory = computed(() => {
      if (!selectedNode.value) return null;
      if (selectedNode.value.type === 'category') {
        return findCategoryById(selectedNode.value.id);
      }
      const found = findSubcategoryById(selectedNode.value.id);
      return found ? found.parent : null;
    });

    const selectedSubcategory = computed(() => {
      if (!selectedNode.value || selectedNode.value.type !== 'subcategory') {
        return null;
      }
      const found = findSubcategoryById(selectedNode.value.id);
      return found ? found.sub : null;
    });

    const triggerLabel = computed(() => {
      if (loadingBudget.value && !selectedNode.value) {
        return 'Loading your budget…';
      }
      if (!selectedNode.value) return '— None / not budgeted —';
      if (selectedSubcategory.value) {
        return `${selectedCategory.value.name} › ${selectedSubcategory.value.name}`;
      }
      return selectedCategory.value?.name || '—';
    });

    // ============================================================
    // BUDGET MATH
    // ============================================================
    const parentDisplayAvailable = (cat) => {
      if (cat.subcategories && cat.subcategories.length > 0) {
        return cat.incSubAvailable;
      }
      return cat.available;
    };

    const subsWithRoom = computed(() => {
      if (!selectedCategory.value) return [];
      const cost = amount.value || 0;
      return (selectedCategory.value.subcategories || [])
        .filter((sub) => sub.available >= cost && sub.budget > 0)
        .slice()
        .sort((a, b) => b.available - a.available);
    });

    // ============================================================
    // SELECTION HANDLERS
    // ============================================================
    const selectNone = () => {
      selectedNode.value = null;
      dropdownOpen.value = false;
    };

    const selectCategory = (cat) => {
      selectedNode.value = { type: 'category', id: cat.categoryID };
      dropdownOpen.value = false;
    };

    const selectSubcategory = (cat, sub) => {
      selectedNode.value = {
        type: 'subcategory',
        id: sub.subcategoryID,
        parentId: cat.categoryID,
      };
      dropdownOpen.value = false;
    };

    const applySuggestion = (suggestion) => {
      if (suggestion.type === 'subcategory') {
        const found = findSubcategoryById(suggestion.targetId);
        if (found) {
          selectSubcategory(found.parent, found.sub);
        }
      }
    };

    const handleClickOutside = (e) => {
      if (dropdownRef.value && !dropdownRef.value.contains(e.target)) {
        dropdownOpen.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      fetchMonthlyBudget();
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    // ============================================================
    // BUDGET SIGNAL
    // ============================================================
    const budgetSignal = computed(() => {
      const cost = amount.value || 0;
      if (!selectedNode.value || cost === 0) {
        return {
          severity: 'pass',
          title: 'Budget',
          message: '',
          passed: true,
        };
      }

      // CASE 1: Subcategory selected
      if (selectedSubcategory.value) {
        const sub = selectedSubcategory.value;
        const parent = selectedCategory.value;
        const title = `${parent.name} › ${sub.name}`;

        if (sub.budget === 0) {
          return {
            severity: 'pass',
            title,
            message: `No budget set for ${sub.name} this month.`,
            passed: true,
          };
        }

        const parentRollup = parent.incSubAvailable;
        const parentNote =
          parentRollup < cost && sub.available >= cost
            ? ` (Note: ${parent.name} as a whole only has $${formatNumber(
                parentRollup
              )} available across all subs.)`
            : '';

        if (cost <= sub.available) {
          return {
            severity: 'pass',
            title,
            message: `Fits — $${formatNumber(
              sub.available - cost
            )} would still be available in ${sub.name}.${parentNote}`,
            passed: true,
          };
        }
        if (
          cost <= sub.available * 1.5 &&
          mode.value === 'oneTime' &&
          sub.available > 0
        ) {
          return {
            severity: 'warn',
            title,
            message: `Tight — exceeds remaining $${formatNumber(
              sub.available
            )} by $${formatNumber(cost - sub.available)}.`,
            passed: false,
          };
        }
        return {
          severity: 'fail',
          title,
          message: `Over budget — $${formatNumber(
            Math.max(0, sub.available)
          )} available, you'd need $${formatNumber(cost)}.`,
          passed: false,
        };
      }

      // CASE 2: Parent category selected
      const cat = selectedCategory.value;
      const hasSubs = (cat.subcategories || []).length > 0;
      const available = parentDisplayAvailable(cat);
      const totalBudget = hasSubs ? cat.incSubBudget : cat.budget;

      if (totalBudget === 0) {
        return {
          severity: 'pass',
          title: cat.name,
          message: `No budget set for ${cat.name} this month.`,
          passed: true,
        };
      }

      if (cost <= available) {
        return {
          severity: 'pass',
          title: cat.name,
          message: `Fits — $${formatNumber(
            available - cost
          )} would remain in ${cat.name}${
            hasSubs ? ' across subcategories' : ''
          }.`,
          passed: true,
        };
      }

      if (hasSubs && subsWithRoom.value.length > 0) {
        const best = subsWithRoom.value[0];
        return {
          severity: 'warn',
          title: cat.name,
          message: `${cat.name} is fully spent overall, but ${best.name} still has $${formatNumber(
            best.available
          )} available — enough to cover this.`,
          passed: false,
        };
      }

      if (
        cost <= available * 1.5 &&
        available > 0 &&
        mode.value === 'oneTime'
      ) {
        return {
          severity: 'warn',
          title: cat.name,
          message: `Tight — only $${formatNumber(
            available
          )} remaining in ${cat.name}.`,
          passed: false,
        };
      }
      return {
        severity: 'fail',
        title: cat.name,
        message: `Significantly over — only $${formatNumber(
          Math.max(0, available)
        )} available in ${cat.name}.`,
        passed: false,
      };
    });

    // ============================================================
    // OTHER SIGNALS
    // ============================================================
    const emergencyFundEnabled = computed(
      () => emergencyFund.value && emergencyFund.value.target > 0
    );
    const hasGoals = computed(() => savingsGoals.value.length > 0);
    const hasDebt = computed(() => debts.value.length > 0);

    const emergencySignal = computed(() => {
      if (!emergencyFundEnabled.value) {
        return { severity: 'pass', message: '', passed: true };
      }
      const ef = emergencyFund.value;
      const cost = mode.value === 'oneTime' ? amount.value : 0;
      const afterPurchase = ef.current - cost;
      const targetRatio = afterPurchase / ef.target;

      if (cost === 0) {
        return {
          severity: 'pass',
          message: `Untouched — recurring purchases don't tap your emergency fund.`,
          passed: true,
        };
      }
      if (afterPurchase >= ef.target) {
        return {
          severity: 'pass',
          message: `Safe — emergency fund stays fully funded after purchase.`,
          passed: true,
        };
      }
      if (targetRatio >= 0.75) {
        return {
          severity: 'warn',
          message: `Slight dip — emergency fund drops to ${Math.round(
            targetRatio * 100
          )}% of target.`,
          passed: false,
        };
      }
      return {
        severity: 'fail',
        message: `Risky — emergency fund drops to ${Math.round(
          targetRatio * 100
        )}% of target. Consider waiting.`,
        passed: false,
      };
    });

    const goalsSignal = computed(() => {
      if (!hasGoals.value) {
        return { severity: 'pass', message: '', passed: true };
      }
      const cost = amount.value || 0;
      if (cost === 0) {
        return { severity: 'pass', message: '', passed: true };
      }

      const sortedGoals = [...savingsGoals.value].sort(
        (a, b) => new Date(a.targetDate) - new Date(b.targetDate)
      );
      const nearest = sortedGoals[0];
      if (!nearest || !nearest.monthlyContribution) {
        return {
          severity: 'pass',
          message: `No active monthly contributions to compare against.`,
          passed: true,
        };
      }

      let monthsDelay = 0;
      if (mode.value === 'oneTime') {
        monthsDelay = cost / nearest.monthlyContribution;
      } else {
        monthsDelay = (cost / nearest.monthlyContribution) * 12;
      }

      if (monthsDelay < 1) {
        return {
          severity: 'pass',
          message: `Minimal impact on "${nearest.name}" — less than a month's delay.`,
          passed: true,
        };
      }
      if (monthsDelay < 3) {
        return {
          severity: 'warn',
          message: `Pushes "${nearest.name}" back roughly ${Math.round(
            monthsDelay
          )} ${monthsDelay >= 2 ? 'months' : 'month'}.`,
          passed: false,
        };
      }
      return {
        severity: 'fail',
        message: `Delays "${nearest.name}" by about ${Math.round(
          monthsDelay
        )} months.`,
        passed: false,
      };
    });

    const debtSignal = computed(() => {
      if (!hasDebt.value) {
        return { severity: 'pass', message: '', passed: true };
      }
      const cost = amount.value || 0;
      if (cost === 0) {
        return { severity: 'pass', message: '', passed: true };
      }

      const totalMonthlyPayment = debts.value.reduce(
        (sum, d) => sum + (d.monthlyPayment || 0),
        0
      );
      if (totalMonthlyPayment === 0) {
        return { severity: 'pass', message: '', passed: true };
      }

      let monthsDelay = 0;
      if (mode.value === 'oneTime') {
        monthsDelay = cost / totalMonthlyPayment;
      } else {
        monthsDelay = (cost / totalMonthlyPayment) * 6;
      }

      if (monthsDelay < 0.5) {
        return {
          severity: 'pass',
          message: `Negligible — won't meaningfully change your debt payoff date.`,
          passed: true,
        };
      }
      if (monthsDelay < 2) {
        return {
          severity: 'warn',
          message: `Could've shaved roughly ${Math.round(
            monthsDelay
          )} ${monthsDelay >= 1.5 ? 'months' : 'month'} off your payoff date.`,
          passed: false,
        };
      }
      return {
        severity: 'fail',
        message: `Equivalent to about ${Math.round(
          monthsDelay
        )} months of extra debt payments.`,
        passed: false,
      };
    });

    const signals = computed(() => ({
      budget: budgetSignal.value,
      emergency: emergencySignal.value,
      goals: goalsSignal.value,
      debt: debtSignal.value,
    }));

    // ============================================================
    // VERDICT
    // ============================================================
    const verdict = computed(() => {
      if (!amount.value || amount.value <= 0) return null;

      const activeSignals = [];
      if (selectedNode.value) activeSignals.push(signals.value.budget);
      if (emergencyFundEnabled.value)
        activeSignals.push(signals.value.emergency);
      if (hasGoals.value) activeSignals.push(signals.value.goals);
      if (hasDebt.value) activeSignals.push(signals.value.debt);

      if (activeSignals.length === 0) {
        return {
          tier: 'neutral',
          icon: 'circle-info',
          title: 'Not enough data',
          summary:
            'Set up a budget category, emergency fund, goal, or debt to get a real verdict.',
          suggestion: null,
        };
      }

      const fails = activeSignals.filter((s) => s.severity === 'fail').length;
      const warns = activeSignals.filter((s) => s.severity === 'warn').length;

      let suggestion = null;
      if (
        selectedNode.value?.type === 'category' &&
        signals.value.budget.severity === 'warn' &&
        subsWithRoom.value.length > 0
      ) {
        const best = subsWithRoom.value[0];
        suggestion = {
          type: 'subcategory',
          targetId: best.subcategoryID,
          targetName: best.name,
          message: `${best.name} has $${formatNumber(
            best.available
          )} available — switch to evaluate this purchase against just that subcategory.`,
        };
      }

      const criticalFail =
        signals.value.emergency.severity === 'fail' ||
        (selectedNode.value && signals.value.budget.severity === 'fail');

      if (criticalFail || fails >= 2) {
        return {
          tier: 'no',
          icon: 'circle-xmark',
          title: 'Probably not right now',
          summary: buildSummary('no'),
          suggestion,
        };
      }
      if (fails >= 1 || warns >= 2) {
        return {
          tier: 'caution',
          icon: 'triangle-exclamation',
          title: 'Yes, but with tradeoffs',
          summary: buildSummary('caution'),
          suggestion,
        };
      }
      return {
        tier: 'yes',
        icon: 'circle-check',
        title: 'Yes, you can afford this',
        summary: buildSummary('yes'),
        suggestion,
      };
    });

    const buildSummary = (tier) => {
      const name = purchaseName.value || 'this purchase';
      if (tier === 'yes') {
        return `${name} fits within your plan without compromising your safety net or goals.`;
      }
      if (tier === 'caution') {
        return `${name} is workable, but it'll cost you somewhere — see the breakdown below.`;
      }
      return `${name} would put real pressure on your finances. Consider waiting or scaling back.`;
    };

    // ============================================================
    // UI HELPERS
    // ============================================================
    const signalTone = (key) => {
      const sev = signals.value[key]?.severity;
      if (sev === 'fail') return 'red';
      if (sev === 'warn') return 'amber';
      return 'green';
    };

    const signalIcon = (key) => {
      const sev = signals.value[key]?.severity;
      if (sev === 'fail') return 'circle-xmark';
      if (sev === 'warn') return 'triangle-exclamation';
      return 'circle-check';
    };

    const parseIcon = (iconString) => {
      if (!iconString) return ['fas', 'circle'];
      const parts = iconString.trim().split(/\s+/);
      return parts.length === 2 ? parts : ['fas', parts[0]];
    };

    const iconStyle = (color) => ({
      backgroundColor: color ? `${color}20` : '#f4f4f6',
      color: color || '#888',
    });

    const formatNumber = (n) => {
      if (n == null || isNaN(n)) return '0';
      return Number(n).toLocaleString('en-US', {
        maximumFractionDigits: 0,
      });
    };

    const resetCalculator = () => {
      purchaseName.value = '';
      amount.value = 0;
      selectedNode.value = null;
    };

    return {
      mode,
      purchaseName,
      amount,
      selectedNode,
      selectedCategory,
      selectedSubcategory,
      loadingBudget,
      loadError,
      dropdownOpen,
      dropdownRef,
      selectNone,
      selectCategory,
      selectSubcategory,
      applySuggestion,
      spendingCategories,
      parentDisplayAvailable,
      triggerLabel,
      emergencyFundEnabled,
      hasGoals,
      hasDebt,
      signals,
      verdict,
      signalTone,
      signalIcon,
      parseIcon,
      iconStyle,
      formatNumber,
      resetCalculator,
      router,
    };
  },
};
</script>

<style scoped>
.affordability-view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Intro */
.intro-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--teal-50);
  border-radius: var(--radius-lg);
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
  border: 1px solid #d8efe6;
}
.dark-theme .intro-card {
  background: #0d2420;
  border-color: #1a3a32;
}
.intro-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent-teal);
  font-size: 14px;
  flex-shrink: 0;
}
.dark-theme .intro-icon {
  background: #1a3a32;
}
.intro-text {
  font-size: 0.8rem;
  color: #213547;
  margin: 0;
  line-height: 1.4;
}
.dark-theme .intro-text {
  color: #d4d2d2;
}

/* Mode toggle */
.mode-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 1.25rem;
  background: #f4f4f6;
  padding: 4px;
  border-radius: var(--radius-pill);
}
.dark-theme .mode-toggle {
  background: #1a2535;
}
.mode-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0.6rem 0.75rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-pill);
  font-family: 'Poppins', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
}
.mode-btn.active {
  background: white;
  color: var(--color-primary);
  box-shadow: var(--shadow-sm);
}
.dark-theme .mode-btn {
  color: #9aa6b8;
}
.dark-theme .mode-btn.active {
  background: #212e3e;
  color: white;
}
.mode-icon {
  font-size: 12px;
}

/* Currency input */
.currency-input {
  position: relative;
}
.currency-symbol {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1em;
  font-weight: 600;
  color: #6b7280;
  pointer-events: none;
  z-index: 1;
}
.dark-theme .currency-symbol {
  color: #9aa6b8;
}
.currency-field {
  padding-left: 1.85rem !important;
  width: 100%;
}

/* Category dropdown trigger */
.category-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  background-image: none !important;
  padding-right: 14px !important;
  font-family: 'Poppins', sans-serif;
  color: #213547;
}
.category-trigger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.dark-theme .category-trigger {
  color: #d4d2d2;
}
.category-trigger.open {
  border-color: var(--color-accent-purple) !important;
  box-shadow: 0 0 0 3px rgba(127, 119, 221, 0.1);
}
.category-trigger-value {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.category-trigger-value.placeholder {
  color: #9ca3af;
}
.dark-theme .category-trigger-value.placeholder {
  color: #6b7e95;
}
.category-chevron {
  font-size: 11px;
  color: #6b7280;
  transition: transform var(--transition-fast);
  flex-shrink: 0;
}
.category-chevron.open {
  transform: rotate(180deg);
}
.dark-theme .category-chevron {
  color: #9aa6b8;
}

/* Inline-expanding menu */
.category-menu {
  margin-top: 6px;
  background: white;
  border: 1.5px solid #e7e7e7;
  border-radius: var(--radius-sm);
  overflow: hidden;
  max-height: 320px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.dark-theme .category-menu {
  background: #212e3e;
  border-color: #2a3a4e;
}

.category-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0.6rem 0.85rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid #f4f4f6;
  border-radius: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  color: #213547;
  text-align: left;
  cursor: pointer;
  transition: background var(--transition-fast);
  -webkit-tap-highlight-color: transparent;
}
.category-option:last-child {
  border-bottom: none;
}
.category-option:hover,
.category-option:active {
  background: #fafafa;
}
.category-option.selected {
  background: var(--purple-50);
  color: var(--color-accent-purple);
  font-weight: 600;
}
.dark-theme .category-option {
  color: #d4d2d2;
  border-bottom-color: #2a3a4e;
}
.dark-theme .category-option:hover,
.dark-theme .category-option:active {
  background: #1a2535;
}
.dark-theme .category-option.selected {
  background: #1e1a40;
}

.category-parent {
  font-weight: 600;
}
.category-sub {
  padding-left: 1.5rem;
  font-size: 0.82rem;
  font-weight: 400;
  background: #fafbfc;
}
.dark-theme .category-sub {
  background: #1a2535;
}

.sub-indent {
  color: #c0c0c8;
  font-size: 0.75rem;
  flex-shrink: 0;
  margin-right: -2px;
}
.dark-theme .sub-indent {
  color: #4a5a6e;
}

.option-icon-wrap {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 11px;
}
.option-icon {
  font-size: 11px;
}

.option-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.option-remaining {
  font-size: 0.7rem;
  color: #6b7280;
  flex-shrink: 0;
  font-weight: 500;
}
.option-remaining.negative {
  color: #d85a30;
}
.dark-theme .option-remaining {
  color: #9aa6b8;
}
.category-option.selected .option-remaining {
  color: var(--color-accent-purple);
}

.category-empty {
  padding: 1rem;
  text-align: center;
  font-size: 0.8rem;
  color: #9ca3af;
}
.dark-theme .category-empty {
  color: #6b7e95;
}

/* Recurring annual cost note */
.recurring-note {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--purple-50);
  border-radius: var(--radius-sm);
  padding: 0.6rem 0.75rem;
  margin-top: 0.85rem;
  font-size: 0.78rem;
  color: #213547;
}
.dark-theme .recurring-note {
  background: #1e1a40;
  color: #d4d2d2;
}
.note-icon {
  font-size: 12px;
  color: var(--color-accent-purple);
}

/* Verdict card */
.verdict-card {
  border-radius: var(--radius-lg);
  padding: 1.1rem 1.15rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid;
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.verdict-card.verdict-yes {
  background: #e8f5ee;
  border-color: #c5e7d4;
}
.verdict-card.verdict-caution {
  background: #fff8e6;
  border-color: #f4e4b8;
}
.verdict-card.verdict-no {
  background: #fdeee7;
  border-color: #f5cdb8;
}
.verdict-card.verdict-neutral {
  background: #f4f4f6;
  border-color: #e5e7eb;
}
.dark-theme .verdict-card.verdict-yes {
  background: #0f2a1c;
  border-color: #1a4030;
}
.dark-theme .verdict-card.verdict-caution {
  background: #2e2718;
  border-color: #4a3e1f;
}
.dark-theme .verdict-card.verdict-no {
  background: #2e1d18;
  border-color: #4a2a20;
}
.dark-theme .verdict-card.verdict-neutral {
  background: #1a2535;
  border-color: #2a3a4e;
}

.verdict-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.verdict-badge {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  color: white;
}
.verdict-badge.verdict-yes {
  background: var(--color-accent-green);
}
.verdict-badge.verdict-caution {
  background: #d4a01a;
}
.verdict-badge.verdict-no {
  background: #d85a30;
}
.verdict-badge.verdict-neutral {
  background: #6b7280;
}
.verdict-text {
  flex: 1;
  min-width: 0;
}
.verdict-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-primary);
  margin: 0 0 4px;
}
.dark-theme .verdict-title {
  color: white;
}
.verdict-summary {
  font-size: 0.8rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.45;
}
.dark-theme .verdict-summary {
  color: #cbd5e1;
}

/* Suggestion */
.verdict-suggestion {
  display: flex;
  gap: 10px;
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
}
.dark-theme .verdict-suggestion {
  border-top-color: rgba(255, 255, 255, 0.1);
}
.suggestion-icon {
  color: var(--color-accent-purple);
  font-size: 14px;
  margin-top: 2px;
  flex-shrink: 0;
}
.suggestion-text {
  flex: 1;
}
.suggestion-message {
  font-size: 0.78rem;
  color: #4b5563;
  margin: 0 0 0.5rem;
  line-height: 1.4;
}
.dark-theme .suggestion-message {
  color: #cbd5e1;
}
.suggestion-action {
  background: none;
  border: none;
  padding: 0;
  color: var(--color-accent-purple);
  font-family: 'Poppins', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}
.suggestion-action:hover {
  color: var(--purple-600);
  text-decoration: underline;
}

/* Signals breakdown */
.signals-card {
  padding: 0 !important;
  overflow: hidden;
}
.signal-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #f8f8f8;
}
.dark-theme .signal-row {
  border-bottom-color: #2a3a4e;
}
.signal-row:last-child {
  border-bottom: none;
}
.signal-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.signal-icon-wrap.green {
  background: var(--teal-50);
}
.signal-icon-wrap.amber {
  background: #fff4d4;
}
.signal-icon-wrap.red {
  background: #fdeee7;
}
.dark-theme .signal-icon-wrap.green {
  background: #0f2a1c;
}
.dark-theme .signal-icon-wrap.amber {
  background: #2e2718;
}
.dark-theme .signal-icon-wrap.red {
  background: #2e1d18;
}
.signal-icon {
  font-size: 13px;
}
.signal-icon-wrap.green .signal-icon {
  color: var(--color-accent-green);
}
.signal-icon-wrap.amber .signal-icon {
  color: #d4a01a;
}
.signal-icon-wrap.red .signal-icon {
  color: #d85a30;
}
.signal-text {
  flex: 1;
  min-width: 0;
}
.signal-title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #213547;
  margin: 0 0 2px;
}
.dark-theme .signal-title {
  color: white;
}
.signal-detail {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
}
.dark-theme .signal-detail {
  color: #9aa6b8;
}
.signal-empty {
  padding: 1.5rem 1rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.5;
}
.signal-empty p {
  margin: 0.5rem 0 0;
}
.signal-empty svg {
  font-size: 18px;
  color: var(--color-accent-purple);
}
</style>
